import { PictureAsPdf } from '@mui/icons-material'
import { Button, Card, CardContent, Chip as MuiChip, Grid, MenuItem, TextField, Divider, TablePagination } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { ClusterShipperDto } from 'dtos/clusterShipperDto'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'

export default function NeedsClusterDeliveredToThirdPartyConversionCompany() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [clusterShippers, setClusterShippers] = useState<ClusterShipperDto[]>()
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingClusterShippers: false,
    isGettingClusterShipperPdf: false
  })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    sortBy: 'clusterShipperNumber',
    sortDirection: 'asc'
  })
  const [totalCount, setTotalCount] = useState<number>(0)

  const getNeedsClusterDeliveredToThirdPartyConversionCompanyToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingClusterShippers', true)
    controllers.toDoLists
      .getNeedsClusterDeliveredToThirdPartyConversionCompanyToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setClusterShippers(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingClusterShippers', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs Cluster Delivered To Third Party Conversion Company To Do List')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingClusterShippers)
  }, [layout, loadingState.isGettingClusterShippers])

  useEffect(() => {
    getNeedsClusterDeliveredToThirdPartyConversionCompanyToDoList(parameters)
  }, [getNeedsClusterDeliveredToThirdPartyConversionCompanyToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto' alignContent='center'>
              <Grid item container xs={12} justifyContent='space-between'>
                <ListFilterGroup>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    margin='dense'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    size='small'
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='clusterShipperNumber'>Cluster Shipper#</MenuItem>

                    <MenuItem value='numberOfClusters'># of Clusters</MenuItem>

                    <MenuItem value='thirdPartyConversionCompany'>3rd Party Conversion Company</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='buyer'>Buyer</MenuItem>
                  </TextField>

                  {[
                    '',
                    'clusterShipperNumber',
                    'numberOfClusters',
                    'thirdPartyConversionCompany',
                    'exporter',
                    'buyer'
                  ].includes(filterField) && (
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        disabled={!filterField}
                        label='Filter Value'
                        margin='dense'
                        onChange={e => {
                          setFilterValue(e.target.value)
                        }}
                        size='small'
                        value={filterValue || ''}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='inherit'
                      disabled={!filterField || !filterValue || !filterField}
                      variant='contained'
                      onClick={() => {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })

                        setFilterField('')
                        setFilterValue('')
                      }}
                    >
                      APPLY FILTER
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='secondary'
                      disabled={
                        !Boolean(
                          parameters.clusterShipperNumber ||
                            parameters.numberOfClusters ||
                            parameters.thirdPartyConversionCompany ||
                            parameters.exporter ||
                            parameters.buyer
                        )
                      }
                      variant='text'
                      onClick={() => {
                        setParameters({
                          ...parameters,
                          clusterShipperNumber: 'undefined',
                          numberOfClusters: undefined,
                          thirdPartyConversionCompany: 'undefined',
                          exporter: 'undefined',
                          buyer: 'undefined'
                        })
                      }}
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>
                </ListFilterGroup>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.clusterShipperNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Cluster Shipper Number Contains "${parameters.clusterShipperNumber}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        clusterShipperNumber: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.numberOfClusters && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Number of Clusters is "${parameters.numberOfClusters}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        numberOfClusters: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.thirdPartyConversionCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Third Party Conversion Company Contains "${parameters.thirdPartyConversionCompany}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        thirdPartyConversionCompany: undefined
                      })
                    }}
                  />
                </Grid>
              )}

              {parameters.exporter && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter Contains "${parameters.exporter}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.buyer && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Buyer Contains "${parameters.buyer}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        buyer: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {clusterShippers?.map(shipper => (
        <Card sx={{ mb: 2, mt: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <LabelField label='Cluster Shipper#' value={shipper?.clusterShipperNumber} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='# of Clusters' value={shipper?.numberOfClusters} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='3rd Party Conversion Company' value={shipper?.clusterConversionCompany?.name} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Exporter' value={shipper?.exporterCompany?.name} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Buyer' value={shipper?.buyerCompany?.name} />
                  </Grid>
                  <Grid>
                    <Button
                      endIcon={<PictureAsPdf />}
                      disabled={loadingState.isGettingClusterShipperPdf}
                      onClick={() => {
                        setLoadingState('isGettingClusterShipperPdf', true)
                        controllers
                          .getClusterShipperPdf(shipper.clusterShipperGuid!)
                          .finally(() => setLoadingState('isGettingClusterShipperPdf', false))
                      }}
                      variant='text'
                    >
                      DOWNLOAD SHIPPER PDF
                    </Button>
                  </Grid>
                  <Grid item xs={12} container justifyContent='flex-end' alignItems='center'>
                    <Button
                      onClick={() => {
                        navigate(
                          `/cluster-shippers/${shipper.clusterShipperGuid}/needs-cluster-delivered-to-third-party-conversion-company/`
                        )
                      }}
                      variant='contained'
                    >
                      DELIVER
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Grid item xs={12} marginTop={1}>
        <ExplanationAccordion>
          || The “Needs Cluster Delivered to 3rd Party Conversion Company” To Do List shows all Cluster Shippers that do not
          have a “3rd Party Conversion Delivery” photo was uploaded. || Pressing “Download Shipper PDF” will download the
          Cluster Shipper PDF to the user’s device. || Pressing “Deliver” will navigate the user to the “3rd Party Cluster
          Delivery Photo (Gallery)” screen.||
        </ExplanationAccordion>
      </Grid>
      <Grid item xs={12}>
        <TablePagination
          component='div'
          count={totalCount}
          onPageChange={(_, page: number) => {
            setParameters({
              ...parameters,
              page
            })
          }}
          onRowsPerPageChange={e => {
            setParameters({
              ...parameters,
              page: 0,
              pageSize: e.target.value as unknown as number
            })
          }}
          page={parameters.page}
          rowsPerPage={parameters.pageSize}
          labelRowsPerPage={
            <Grid container spacing={1}>
              <Grid item>Order By:</Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, orderBy: e.target.value })
                  }}
                  select
                  size='small'
                  value={parameters.orderBy}
                  variant='standard'
                >
                  <MenuItem value='clusterShipperNumber'>Cluster Shipper#</MenuItem>

                  <MenuItem value='numberOfClusters'># of Clusters</MenuItem>

                  <MenuItem value='thirdPartyConversionCompany'>3rd Party Conversion Company</MenuItem>

                  <MenuItem value='exporter'>Exporter</MenuItem>

                  <MenuItem value='buyer'>Buyer</MenuItem>
                </TextField>
              </Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                  }}
                  select
                  size='small'
                  value={parameters.order}
                  variant='standard'
                >
                  <MenuItem value='asc'>Ascending</MenuItem>

                  <MenuItem value='desc'>Descending</MenuItem>
                </TextField>
              </Grid>

              <Grid item sx={{ alignSelf: 'stretch' }}>
                <Divider orientation='vertical' />
              </Grid>

              <Grid item>Cards Per Page:</Grid>
            </Grid>
          }
          rowsPerPageOptions={[
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 }
          ]}
          SelectProps={{ fullWidth: false }}
        />
      </Grid>
    </>
  )
}
